import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
  TextField,
  Slide,
} from '@mui/material';
import Button from '@mui/material/Button';
import { Cancel, ThumbUp, ThumbDown } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import { forwardRef, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cardHeaderSx, cardSx, mobileCardSx } from 'utils/styleProps';
import { FileLink, Job, JobType } from 'types';
import {
  formatLicensePlateString,
  formatVehicleString,
  formatViolationsString,
} from 'utils/stringFormatters';
import {
  getShortFormattedAddress,
  formatDateTimeString,
  formatPhoneNumber,
  capitalize,
} from 'utils';
import DigitalSignatureWidget from 'components/widgets/DigitalSignatureWidget';
import { AppContext } from 'context/appContext';
import { TransitionProps } from '@mui/material/transitions';

const Transition = forwardRef(function Transition(
      props: TransitionProps & {
        children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface JobReviewDialogProps {
  isOpen: boolean;
  handleCancel: () => void;
  job: Job;
  handleJobApproval: (jobID: number) => void;
  handleJobDeny: (jobID: number, reason: string) => void;
}

const JobReviewDialog = ({
  isOpen,
  handleCancel,
  job,
  handleJobApproval,
  handleJobDeny,
}: JobReviewDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const { state } = useContext(AppContext);
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const [signature, setSignature] = useState<FileLink | null>(null);
  const [denialReason, setDenialReason] = useState<string>('');

  const onSignatureApplied = (applied: boolean) => {
    if (applied) {
      setSignature(state.signature);
    } else {
      setSignature(null);
    }
  };

  const onDenyClick = () => {
    handleJobDeny(job.id, denialReason);
    setDenialReason('');
  };

  const onApproveClick = () => {
    handleJobApproval(job.id);
    setDenialReason('');
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      aria-label={`${t('permit.invite.header')} Dialog`}
      TransitionComponent={Transition}
      fullScreen={isMobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
      maxWidth={'sm'}
    >
      <Card sx={isMobile ? mobileCardSx : cardSx}>
        <Box
          sx={
            isMobile
              ? {
                  display: 'flex',
                  p: { xs: 2, sm: 4 },
                  backgroundColor: palette.secondary.main,
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100%',
                }
              : cardHeaderSx
          }
        >
          <Box sx={{ flex: 3, alignItems: 'center', display: 'flex' }}>
            <Typography
              variant={'h4'}
              sx={{
                color: palette.secondary.contrastText,
                textAlign: 'left',
              }}
            >
              {t('dialogs.approval.header', {
                ns: 'jobs',
                type: capitalize(job.type),
              })}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              variant={'text'}
              onClick={handleCancel}
              sx={{
                px: 1,
                textTransform: 'uppercase',
                color: palette.secondary.contrastText,
              }}
              endIcon={
                <Cancel
                  htmlColor={palette.secondary.contrastText}
                  fontSize={'large'}
                />
              }
            >
              {t('close')}
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            p: 2,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Grid container spacing={0}>
            <Grid
              size={{
                xs: 6,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('job.property.label', {
                ns: 'entities',
              })}:`}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 8,
              }}
            >
              <Typography variant={'body1'} fontWeight={'bold'}>
                {job.propertyName}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('address')}:`}</Typography>
            </Grid>
            <Grid
              sx={{ mb: { xs: 2, lg: 0 } }}
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <Typography variant={'body1'} fontWeight={'bold'}>
                {getShortFormattedAddress(job.location) || '--'}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('labels.created_by', {
                ns: 'jobs',
              })}:`}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 8,
              }}
            >
              <Typography
                variant={'body1'}
                fontWeight={'bold'}
                textTransform={'capitalize'}
              >
                {job.createdByUserName}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('job.contactPhone', {
                ns: 'entities',
              })}:`}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 8,
              }}
            >
              <Typography variant={'body1'} fontWeight={'bold'}>
                {formatPhoneNumber(job.data.contactPhone || '')}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('job.requestTime', {
                ns: 'entities',
              })}:`}</Typography>
            </Grid>
            <Grid
              sx={{ mb: 2 }}
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <Typography variant={'body1'} fontWeight={'bold'}>
                {formatDateTimeString(job.requestTime) || 'ASAP'}
              </Typography>
            </Grid>
            {job.type !== JobType.PATROL && (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <Typography variant={'h6'}>{`${t('job.data.violations.label', {
                  ns: 'entities',
                })}:`}</Typography>
              </Grid>
            )}
            {job.type !== JobType.PATROL && (
              <Grid
                sx={{ mb: { xs: 2, lg: 0 } }}
                size={{
                  xs: 12,
                  sm: 8,
                }}
              >
                <Typography variant={'body1'} fontWeight={'bold'}>
                  {formatViolationsString(job.data.violations || []) || '--'}
                </Typography>
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('job.vehicle.label', {
                ns: 'entities',
              })}:`}</Typography>
            </Grid>
            <Grid
              sx={{ mb: { xs: 2, lg: 0 } }}
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <Typography variant={'body1'} fontWeight={'bold'}>
                {formatVehicleString(job.vehicle) || '--'}
              </Typography>
            </Grid>
            {job.vehicle?.vin && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 4,
                  }}
                >
                  <Typography variant={'h6'}>{`${t('vehicle.vin', {
                    ns: 'entities',
                  })}:`}</Typography>
                </Grid>
                <Grid
                  sx={{ mb: { xs: 2, lg: 0 } }}
                  size={{
                    xs: 12,
                    sm: 8,
                  }}
                >
                  <Typography variant={'body1'} fontWeight={'bold'}>
                    {job.vehicle?.vin || '--'}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('job.vehicle.plate.label', {
                ns: 'entities',
              })}:`}</Typography>
            </Grid>
            <Grid
              sx={{ mb: 2 }}
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <Typography variant={'body1'} fontWeight={'bold'}>
                {formatLicensePlateString(job.vehicle?.plate) || '--'}
              </Typography>
            </Grid>

            {job.data.notes && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 4,
                  }}
                >
                  <Typography variant={'h6'}>{`${t('job.data.notes', {
                    ns: 'entities',
                  })}:`}</Typography>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 8,
                  }}
                  sx={{ mb: 2 }}
                >
                  <Typography variant={'body1'} fontWeight={'bold'}>
                    {job.data.notes || '--'}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Typography variant={'h6'}>{`${t('job.signatures.label', {
                ns: 'entities',
              })}:`}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
              sx={{ minHeight: 75 }}
            >
              <DigitalSignatureWidget onSignatureApplied={onSignatureApplied} />
            </Grid>
          </Grid>

          <Stack
            width='100%'
            direction='row'
            spacing={2}
            justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
            display={{ xs: 'none', md: 'flex' }}
            alignItems='center'
            mt={2}
          >
            <TextField
              name='denialReason'
              label={t('dialogs.approval.denial_reason', { ns: 'jobs' })}
              value={denialReason}
              disabled={!!signature}
              color={'secondary'}
              margin='none'
              fullWidth
              sx={{
                pr: 0,
                height: 53,
              }}
              onChange={(e) => setDenialReason(e.target.value)}
              inputProps={{
                style: {
                  height: 53,
                },
              }}
              InputProps={{
                sx: {
                  pr: 0,
                  height: 53,
                },
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ borderLeft: `1px solid ${palette.secondary.main}` }}
                  >
                    <Button
                      variant={'text'}
                      onClick={onDenyClick}
                      color={'error'}
                      startIcon={<ThumbDown />}
                      disabled={!denialReason || !!signature}
                      sx={{
                        height: 53,
                        width: 120,
                        m: 0,
                        px: 2,
                        borderRadius: '0px',
                      }}
                    >
                      {t('dialogs.approval.deny', { ns: 'jobs' })}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              sx={{
                flex: { xs: 1, lg: 'none' },
                borderRadius: '4px',
                px: 2,
                width: 120,
                height: 53,
              }}
              variant={'contained'}
              color={'secondary'}
              endIcon={<ThumbUp />}
              onClick={onApproveClick}
              disabled={!signature}
            >
              {t('dialogs.approval.approve', { ns: 'jobs' })}
            </Button>
          </Stack>
        </Box>
      </Card>
    </Dialog>
  );
};
export default JobReviewDialog;
